import { HTMLAttributes, memo } from 'react';

import { Box, FormControl, FormHelperText, MenuItem, Select, SxProps, Theme } from '@mui/material';

import { useLanguage } from '../../hooks';
import { getTimeIntervalAsLabel } from './activityPeriodTimeSetting';
import { CustomTimePicker } from './customTimePicker';

/**
 * Pre-DS-3846 workday selector: a fixed duration of 8 or 12 h and an interval
 * from four values.
 *
 * It is a faithful copy of what master had and is only mounted while the
 * CustomWorkdayDuration flag is off. It gets deleted together with the flag, so
 * nothing should be fixed here: any behaviour change belongs in
 * activityPeriodTimeSetting.tsx, which is the version that stays.
 *
 * getTimeIntervalAsLabel is imported rather than duplicated because both
 * versions share it unchanged.
 */

/**
 * Values an activity question was born with before DS-3846. They have to be
 * restored while the flag is off: the empty defaults DS-3846 introduced would
 * leave the selector below with no option selected.
 */
export const LEGACY_ACTIVITY_PERIOD_TIME_DEFAULTS = {
  workingTime: '720',
  intervalDefault: '30',
};

const customStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '50%',
    margin: 'auto',
  },
  formControlText: {
    marginTop: '12px',
    marginLeft: '0px',
  },
  select: {
    height: '34px',
  },
  menuItem: { fontSize: '12px' },
} satisfies Record<string, SxProps<Theme>>;

// Same type detour as in activityPeriodTimeSetting.tsx: SelectDisplayProps is
// typed as HTMLAttributes, which does not accept data-* attributes.
const displayTestId = (id: string) => ({ 'data-testid': id }) as HTMLAttributes<HTMLDivElement>;

interface ActivityPeriodTimeSettingLegacyProps {
  questionId: string;
  startTime: string;
  onActivityPeriodTimeSettingChange: (
    id: string,
    value: string,
    action: string,
    tableId?: string,
  ) => void;
  tableId?: string;
  workingTime: string;
  intervalDefault: string;
}

const ActivityPeriodTimeSettingLegacy = memo((props: ActivityPeriodTimeSettingLegacyProps) => {
  const {
    questionId,
    startTime,
    onActivityPeriodTimeSettingChange,
    tableId,
    workingTime,
    intervalDefault,
  } = props;
  const lang = useLanguage();
  const workingTimeOptions = ['480', '720'];

  const intervalDefaultOptions = ['10', '15', '30', '60'];

  return (
    <Box sx={customStyles.root} data-testid="activity-period-time-legacy">
      <FormControl>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.activityStartTime}
        </FormHelperText>
        <CustomTimePicker
          value={startTime}
          onChange={(value) =>
            onActivityPeriodTimeSettingChange(questionId, value, 'startTime', tableId)
          }
        />
      </FormControl>

      <FormControl>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.activityDuration}
        </FormHelperText>
        <Select
          disableUnderline
          value={workingTime}
          sx={customStyles.select}
          SelectDisplayProps={displayTestId('activity-legacy-duration')}
          onChange={(e) =>
            onActivityPeriodTimeSettingChange(
              questionId,
              e.target.value.toString(),
              'workingTime',
              tableId,
            )
          }
        >
          {workingTimeOptions.map((workingTimeOption, index) => (
            <MenuItem
              key={index}
              sx={customStyles.menuItem}
              value={parseInt(workingTimeOption)}
              data-testid={`activity-legacy-duration-option-${workingTimeOption}`}
            >
              {getTimeIntervalAsLabel(lang, parseInt(workingTimeOption))}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.activityInterval}
        </FormHelperText>
        <Select
          disableUnderline
          value={intervalDefault}
          sx={customStyles.select}
          SelectDisplayProps={displayTestId('activity-legacy-interval')}
          onChange={(e) =>
            onActivityPeriodTimeSettingChange(
              questionId,
              e.target.value.toString(),
              'intervalDefault',
              tableId,
            )
          }
        >
          {intervalDefaultOptions.map((intervalDefaultOption, index) => (
            <MenuItem
              key={index}
              sx={customStyles.menuItem}
              value={parseInt(intervalDefaultOption)}
              data-testid={`activity-legacy-interval-option-${intervalDefaultOption}`}
            >
              {lang.form_builder.every}{' '}
              {getTimeIntervalAsLabel(lang, parseInt(intervalDefaultOption))}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
});

ActivityPeriodTimeSettingLegacy.displayName = 'ActivityPeriodTimeSettingLegacy';
export { ActivityPeriodTimeSettingLegacy };
